import {ArrowDownLeftIcon, BotMessageSquareIcon, ChevronDown, Maximize2, Minimize2, X} from "lucide-react";
import {useCallback, useEffect, useRef, useState} from "react";

import {ChatInterface} from "@components/ChatInterface";
import {useAgents} from "@contexts/AgentContext";
import {useChatContext} from "@contexts/ChatContext";

const TAB_AGENT_MAP: Record<string, string> = {
    pnl: "P&L Forecast",
    forecast: "P&L Forecast",
    cashflow: "Cash Flow",
};

const DEFAULT_AGENT = "Virtual CFO";

function resolveAgent(
    agents: {slug: string; name: string}[],
    activeTab: string,
    selectedAgentSlug: string | null,
    isVNext: boolean,
) {
    if (selectedAgentSlug) {
        const target = selectedAgentSlug;
        const matched = agents.find((a) => a.slug === target)
            ?? agents.find((a) => a.name.toLowerCase().includes(target.toLowerCase()));
        if (matched) return matched;
    }
    if (isVNext) {
        const vnextAgent = agents.find((a) => a.name.toLowerCase().includes("virtual cfo"));
        if (vnextAgent) return vnextAgent;
    }
    const target = TAB_AGENT_MAP[activeTab] ?? DEFAULT_AGENT;
    const matched = agents.find((a) => a.slug === target)
        ?? agents.find((a) => a.name.toLowerCase().includes(target.toLowerCase()));
    return matched ?? agents[0] ?? null;
}

export function SidePanelChat() {
    const {
        activeTab,
        setSidePanelOpen,
        chatFullscreen,
        setChatFullscreen,
        selectedAgentSlug,
        setSelectedAgentSlug,
    } = useChatContext();
    const {agents, loading} = useAgents();

    const [dropdownOpen, setDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const [isVNext, setIsVNext] = useState(false);
    useEffect(() => {
        const handler = (e: Event) => {
            setIsVNext(!!(e as CustomEvent).detail?.vnext);
        };
        window.addEventListener("dashboard-data-loaded", handler);
        return () => window.removeEventListener("dashboard-data-loaded", handler);
    }, []);

    const currentAgent = resolveAgent(agents, activeTab, selectedAgentSlug, isVNext);

    // Track visited agent slugs so we keep their ChatInterface mounted
    const [visitedSlugs, setVisitedSlugs] = useState<Set<string>>(new Set());
    useEffect(() => {
        if (currentAgent?.slug) {
            setVisitedSlugs((prev) => {
                if (prev.has(currentAgent.slug)) return prev;
                const next = new Set(prev);
                next.add(currentAgent.slug);
                return next;
            });
        }
    }, [currentAgent?.slug]);

    // Close dropdown on outside click
    useEffect(() => {
        if (!dropdownOpen) return;
        const handler = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [dropdownOpen]);

    const handleFullscreen = useCallback(() => {
        setChatFullscreen(!chatFullscreen);
    }, [chatFullscreen, setChatFullscreen]);

    const agentName = currentAgent?.name || "Pecunio";

    const chatBody = (
        <div className="flex-1 overflow-hidden min-h-0">
            {agents
                .filter((a) => visitedSlugs.has(a.slug))
                .map((a) => (
                    <div
                        key={a.slug}
                        className={a.slug === currentAgent?.slug ? "h-full" : "hidden"}
                    >
                        <ChatInterface agent={a} loading={loading} containerMode={chatFullscreen ? "fullscreen" : "floating"} />
                    </div>
                ))}
            {!currentAgent && loading && (
                <div className="flex items-center justify-center h-full">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#209DB3]" />
                </div>
            )}
        </div>
    );

    // ── Fullscreen layout: agent sidebar + centered chat ──────────
    if (chatFullscreen) {
        return (
            <div className="flex flex-1 overflow-hidden">
                {/* Agent sidebar */}
                <div className="w-[220px] flex-shrink-0 bg-[#0F172A] border-r border-white/[0.06] flex flex-col">
                    {/* Logo header */}
                    <div className="px-4 py-5 border-b border-white/[0.06]">
                        <div className="flex items-center gap-2.5">
                            <div className="w-[32px] h-[32px] rounded-lg bg-gradient-to-br from-[#209DB3] to-[#1A8C9E] flex items-center justify-center text-white font-extrabold text-sm">
                                P
                            </div>
                            <div>
                                <div className="text-sm font-bold text-white tracking-wide">PECUNIO AI</div>
                                <div className="text-[10px] text-[#5BF6C8] font-medium">Agent Chat</div>
                            </div>
                        </div>
                    </div>

                    {/* Agent list */}
                    <div className="flex-1 overflow-y-auto py-2">
                        <div className="px-3 py-1.5 text-[10px] font-semibold text-[#94A3B8] uppercase tracking-wider">AI Agents</div>
                        {agents.map((a) => (
                            <button
                                key={a.slug}
                                className={`w-full flex items-center gap-2.5 px-3 py-2 text-[12.5px] transition-colors cursor-pointer ${
                                    a.slug === currentAgent?.slug
                                        ? "bg-white/[0.08] text-[#209DB3] font-semibold"
                                        : "text-[#CBD5E1] hover:bg-white/[0.04]"
                                }`}
                                onClick={() => setSelectedAgentSlug(a.slug)}
                            >
                                <BotMessageSquareIcon className="size-4 flex-shrink-0" />
                                <span className="truncate">{a.name}</span>
                            </button>
                        ))}
                    </div>

                    {/* Exit fullscreen */}
                    <div className="px-3 py-3 border-t border-white/[0.06]">
                        <button
                            onClick={handleFullscreen}
                            className="w-full flex items-center gap-2 px-2.5 py-2 rounded-md text-[11px] text-[#94A3B8] hover:bg-white/[0.06] hover:text-white transition-colors cursor-pointer"
                        >
                            <Minimize2 className="size-3.5" />
                            <span>Exit fullscreen</span>
                        </button>
                    </div>
                </div>

                {/* Centered chat area */}
                <div className="flex-1 flex flex-col overflow-hidden relative">
                    <button
                        onClick={handleFullscreen}
                        title="Close fullscreen"
                        className="absolute top-3 right-4 z-10 w-8 h-8 rounded-md flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer"
                    >
                        <X className="size-4" />
                    </button>
                    {chatBody}
                </div>
            </div>
        );
    }

    // ── Docked side panel layout ──────────────────────────────────
    return (
        <div className="flex flex-col flex-1 overflow-hidden">
            {/* Header: Logo + title + controls */}
            <div
                className="flex items-center gap-2.5 px-3.5 py-3 border-b border-white/[0.06] min-h-[50px]"
                data-tour="agent-panel-header"
            >
                <div className="w-[28px] h-[28px] rounded-[7px] bg-gradient-to-br from-[#209DB3] to-[#1A8C9E] flex items-center justify-center text-white font-extrabold text-[12px] flex-shrink-0">
                    P
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-xs font-bold text-white">Agent Chat</div>
                    <div className="text-[10px] text-[#5BF6C8] font-medium mt-px">Context-aware financial assistant</div>
                </div>
                <button
                    onClick={() => setSidePanelOpen(false)}
                    title="Minimise chat"
                    className="w-[26px] h-[26px] rounded-[5px] border border-white/10 bg-transparent text-[#94A3B8] hover:border-[#209DB3] hover:text-[#209DB3] flex items-center justify-center transition-all cursor-pointer"
                >
                    <ArrowDownLeftIcon className="size-[11px]" />
                </button>
                <button
                    onClick={handleFullscreen}
                    title="Fullscreen chat"
                    className="w-[26px] h-[26px] rounded-[5px] border border-white/10 bg-transparent text-[#94A3B8] hover:border-[#209DB3] hover:text-[#209DB3] flex items-center justify-center transition-all cursor-pointer"
                >
                    <Maximize2 className="size-[11px]" />
                </button>
            </div>

            {/* Agent selector dropdown (docked only) */}
            <div
                className="flex items-center gap-2 px-3.5 py-2.5 border-b border-white/[0.06]"
                data-tour="agent-panel-selector"
            >
                <div className="w-7 h-7 rounded-[7px] bg-gradient-to-br from-[#209DB3] to-[#1590A0] flex items-center justify-center text-[8px] text-white font-bold tracking-wide flex-shrink-0">
                    AI
                </div>
                <div ref={dropdownRef} className="relative flex-1 min-w-0">
                    <button
                        onClick={() => setDropdownOpen(!dropdownOpen)}
                        className="w-full flex items-center gap-1 bg-transparent border border-white/10 rounded-md px-2.5 py-1.5 text-white text-[11.5px] font-semibold cursor-pointer hover:border-white/20 transition-colors"
                    >
                        <span className="truncate flex-1 text-left">{agentName}</span>
                        <ChevronDown className="size-3 opacity-60 flex-shrink-0" />
                    </button>
                    {dropdownOpen && agents.length > 0 && (
                        <div className="absolute top-full left-0 mt-1 z-50 bg-[#0F172A] border border-white/10 rounded-md shadow-lg py-1 min-w-full">
                            {agents.map((a) => (
                                <button
                                    key={a.slug}
                                    className={`w-full text-left px-3 py-1.5 text-[11px] hover:bg-white/[0.06] ${
                                        a.slug === currentAgent?.slug ? "font-semibold text-[#209DB3]" : "text-[#E2E8F0]"
                                    }`}
                                    onClick={() => {
                                        setSelectedAgentSlug(a.slug);
                                        setDropdownOpen(false);
                                    }}
                                >
                                    {a.name}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Chat body */}
            {chatBody}
        </div>
    );
}
